import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/** Connection settings, persisted in localStorage so a refresh keeps you logged in. */
interface Conn {
  base: string; // API origin; '' = same origin (nginx path-proxies /v1 + /health)
  token: string;
}

const LS_BASE = 'cati.base';
const LS_TOKEN = 'cati.token';

function load(): Conn {
  return {
    base: localStorage.getItem(LS_BASE) ?? '',
    token: localStorage.getItem(LS_TOKEN) ?? '',
  };
}

export interface ApiResult<T = unknown> {
  ok: boolean;
  status: number;
  data: T;
}

interface ApiCtx extends Conn {
  setConn: (c: Partial<Conn>) => void;
  request: <T = unknown>(method: string, path: string, body?: unknown) => Promise<ApiResult<T>>;
  /** GET a file (export) and trigger a browser download. */
  download: (path: string, fallbackName: string) => Promise<{ ok: boolean; status: number }>;
}

const Ctx = createContext<ApiCtx | null>(null);

export function ApiProvider({ children }: { children: ReactNode }) {
  const [conn, setConnState] = useState<Conn>(load);

  const setConn = useCallback((c: Partial<Conn>) => {
    setConnState((prev) => {
      const next = { ...prev, ...c };
      localStorage.setItem(LS_BASE, next.base);
      localStorage.setItem(LS_TOKEN, next.token);
      return next;
    });
  }, []);

  const request = useCallback(
    async <T,>(method: string, path: string, body?: unknown): Promise<ApiResult<T>> => {
      const headers: Record<string, string> = {};
      if (conn.token) headers.Authorization = `Bearer ${conn.token}`;
      // FormData sets its own multipart Content-Type (with boundary); leave it alone.
      const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
      if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
      const res = await fetch(conn.base.replace(/\/$/, '') + path, {
        method,
        headers,
        body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
      });
      let data: unknown = null;
      const text = await res.text();
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = text;
      }
      return { ok: res.ok, status: res.status, data: data as T };
    },
    [conn.base, conn.token],
  );

  const download = useCallback(
    async (path: string, fallbackName: string): Promise<{ ok: boolean; status: number }> => {
      const headers: Record<string, string> = {};
      if (conn.token) headers.Authorization = `Bearer ${conn.token}`;
      const res = await fetch(conn.base.replace(/\/$/, '') + path, { headers });
      if (!res.ok) return { ok: false, status: res.status };
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const name = /filename="?([^"]+)"?/.exec(cd)?.[1] ?? fallbackName;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      return { ok: true, status: res.status };
    },
    [conn.base, conn.token],
  );

  const value = useMemo<ApiCtx>(() => ({ ...conn, setConn, request, download }), [conn, setConn, request, download]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApi(): ApiCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useApi must be used within <ApiProvider>');
  return c;
}

export type KeyCheck = 'ok' | 'bad' | 'unreachable';

/**
 * Validate a base+token pair against an authed endpoint. /health is unauthed so
 * it can't verify the key; a small admin list can. 401/403 ⇒ bad key; any other
 * reachable response ⇒ the key was accepted (even a 500 means we're past auth).
 */
export async function validateKey(base: string, token: string): Promise<KeyCheck> {
  if (!token) return 'bad';
  try {
    const res = await fetch(base.replace(/\/$/, '') + '/v1/admin/cache?limit=1', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 401 || res.status === 403) return 'bad';
    return 'ok';
  } catch {
    return 'unreachable';
  }
}
